import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, fieldErrors, unwrap, type Schemas } from '../../api/client';
import { useAssetTypes } from '../../api/queries';
import type { AssetDetail, AssetStatus } from '../../api/types';
import { formValueToSpec, SpecInput, specToFormValue, specVisible } from '../../components/assets/SpecInput';
import { EmployeeSelect, LocationSelect } from '../../components/pickers';
import { Button, Card, CardBody, CardHeader, Checkbox, DateInput, ErrorBox, Field, FormGrid, Input, Loading, Notice, nullIfEmpty, PageHeader, Select, Textarea, useToast } from '../../components/ui';
import { centsToInput, counterAmount, parseMoney, STATUS_LABELS, todayIso, VAT_PERCENT } from '../../lib/format';

type FormState = {
  asset_number: string;
  asset_type: string;
  tracking_type: 'asset' | 'component';
  name: string;
  description: string;
  manufacturer: string;
  model: string;
  serial_number: string;
  manufacturer_part_number: string;
  service_tag: string;
  notes: string;
  status: Exclude<AssetStatus, 'disposed'>;
  employee: string;
  location: string;
  effective_date: string;
  purchase_date: string;
  capitalization_date: string;
  commissioning_date: string;
  cost_net: string;
  cost_gross: string;
  supplier: string;
  invoice_number: string;
  order_number: string;
  warranty_until: string;
  accounting_asset_number: string;
  photo_not_required: boolean;
  exclude_from_reconciliation: boolean;
};

const TEXT_FIELDS = [
  'description',
  'manufacturer',
  'model',
  'serial_number',
  'manufacturer_part_number',
  'service_tag',
  'notes',
  'purchase_date',
  'capitalization_date',
  'commissioning_date',
  'supplier',
  'invoice_number',
  'order_number',
  'warranty_until',
  'accounting_asset_number',
] as const;

const emptyForm = (): FormState => ({
  asset_number: '',
  asset_type: '',
  tracking_type: 'asset',
  name: '',
  description: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  manufacturer_part_number: '',
  service_tag: '',
  notes: '',
  status: 'in_stock',
  employee: '',
  location: '',
  effective_date: todayIso(),
  purchase_date: '',
  capitalization_date: '',
  commissioning_date: '',
  cost_net: '',
  cost_gross: '',
  supplier: '',
  invoice_number: '',
  order_number: '',
  warranty_until: '',
  accounting_asset_number: '',
  photo_not_required: false,
  exclude_from_reconciliation: false,
});

const fromAsset = (a: AssetDetail): FormState => ({
  ...emptyForm(),
  asset_number: a.asset_number,
  asset_type: a.asset_type.key,
  tracking_type: a.tracking_type,
  name: a.name,
  ...Object.fromEntries(TEXT_FIELDS.map((k) => [k, a[k] ?? ''])),
  cost_net: centsToInput(a.cost_net_cents),
  cost_gross: centsToInput(a.cost_gross_cents),
  photo_not_required: a.photo_not_required,
  exclude_from_reconciliation: a.exclude_from_reconciliation,
});

/** Technische Felder, die je Gerät eindeutig sind und beim Kopieren leer bleiben. */
const UNIQUE_SPEC = /(^|_)(hostname|mac|ip|ip_address|address|imei|phone_number|license_key|serial)(_|$)/;

/** Vorlage aus einem bestehenden Asset: gleiche Daten, aber ohne Nummern, die je Gerät eindeutig sind. */
const copyFrom = (a: AssetDetail): FormState => ({
  ...fromAsset(a),
  asset_number: '',
  serial_number: '',
  service_tag: '',
  accounting_asset_number: '',
  notes: '',
  status: 'in_stock',
  employee: '',
  location: a.location?.id ?? '',
  effective_date: todayIso(),
});

export function AssetFormPage() {
  const { ref } = useParams();
  const isEdit = !!ref;
  const [params] = useSearchParams();
  const copyRef = isEdit ? null : params.get('copy');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const types = useAssetTypes();

  const existing = useQuery({
    queryKey: ['asset', ref],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}', { params: { path: { ref: ref! } } })),
    enabled: isEdit,
  });

  const [form, setForm] = useState<FormState>(emptyForm);
  const [specs, setSpecs] = useState<Record<string, string>>({});
  const [initialSpecs, setInitialSpecs] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(!isEdit && !copyRef);

  // „Kopieren“: Formular aus einem bestehenden Asset vorausfüllen – gespeichert wird erst mit „Anlegen“.
  const source = useQuery({
    queryKey: ['asset', copyRef],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}', { params: { path: { ref: copyRef! } } })),
    enabled: !!copyRef,
  });
  useEffect(() => {
    if (copyRef && source.data && !loaded) {
      setForm(copyFrom(source.data));
      setSpecs(Object.fromEntries(source.data.specs.filter((x) => !UNIQUE_SPEC.test(x.key)).map((x) => [x.key, specToFormValue(x.value)])));
      setLoaded(true);
    }
  }, [copyRef, source.data, loaded]);

  useEffect(() => {
    if (isEdit && existing.data && !loaded) {
      setForm(fromAsset(existing.data));
      const s = Object.fromEntries(existing.data.specs.map((x) => [x.key, specToFormValue(x.value)]));
      setSpecs(s);
      setInitialSpecs(s);
      setLoaded(true);
    }
  }, [isEdit, existing.data, loaded]);

  const type = useMemo(() => types.data?.find((t) => t.key === form.asset_type), [types.data, form.asset_type]);
  const specDefs = (type?.specs ?? []).filter((s) => s.is_active).sort((a, b) => a.sort_order - b.sort_order);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Netto ↔ Brutto (19 % USt.): das andere Feld wird gefüllt, solange es leer ist oder automatisch berechnet wurde
  const [autoCost, setAutoCost] = useState<'net' | 'gross' | null>(null);
  const setCost = (changed: 'net' | 'gross', value: string) => {
    const key = changed === 'net' ? 'cost_net' : 'cost_gross';
    const otherKey = changed === 'net' ? 'cost_gross' : 'cost_net';
    const otherKind = changed === 'net' ? 'gross' : 'net';
    const fillOther = autoCost === otherKind || !form[otherKey].trim();
    setForm((f) => ({ ...f, [key]: value, ...(fillOther ? { [otherKey]: counterAmount(changed, value) } : {}) }));
    // Ein automatisch gefülltes Feld, das man selbst ändert, bleibt danach so
    setAutoCost(fillOther ? otherKind : autoCost === changed ? null : autoCost);
  };

  const save = useMutation({
    mutationFn: async () => {
      const net = parseMoney(form.cost_net);
      const gross = parseMoney(form.cost_gross);
      if (Number.isNaN(net) || Number.isNaN(gross)) throw new Error('Bitte Beträge im Format 1.234,56 eingeben.');
      const text = Object.fromEntries(TEXT_FIELDS.map((k) => [k, nullIfEmpty(form[k])])) as Record<(typeof TEXT_FIELDS)[number], string | null>;
      const base = {
        asset_type: form.asset_type,
        tracking_type: form.tracking_type,
        name: form.name.trim(),
        ...text,
        cost_net_cents: net,
        cost_gross_cents: gross,
        photo_not_required: form.photo_not_required,
        exclude_from_reconciliation: form.exclude_from_reconciliation,
      };
      if (!isEdit) {
        const specValues: Record<string, string | number | boolean> = {};
        for (const d of specDefs) {
          if (!specVisible(d.key, specs)) continue;
          const v = formValueToSpec(d, specs[d.key] ?? '');
          if (v !== null) specValues[d.key] = v;
        }
        const body: Schemas['CreateAsset'] = {
          ...base,
          status: form.status,
          employee: form.employee || undefined,
          location: form.location || undefined,
          effective_date: form.effective_date || undefined,
          asset_number: form.asset_number.trim() || undefined,
          specs: specValues,
        };
        return unwrap(api.POST('/api/assets', { body }));
      }
      const specChanges: Record<string, string | number | boolean | null> = {};
      for (const d of specDefs) {
        // Ausgeblendete Felder (z. B. AMT ohne Intel-CPU) werden geleert
        const now = specVisible(d.key, specs) ? (specs[d.key] ?? '') : '';
        if (now !== (initialSpecs[d.key] ?? '')) specChanges[d.key] = formValueToSpec(d, now);
      }
      const body: Schemas['UpdateAsset'] = { ...base, version: existing.data!.version, specs: specChanges };
      return unwrap(api.PATCH('/api/assets/{ref}', { params: { path: { ref: ref! } }, body }));
    },
    onSuccess: (asset) => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: ['asset'] });
      toast(isEdit ? 'Änderungen gespeichert' : `Asset ${asset.asset_number} angelegt`);
      navigate(`/assets/${asset.asset_number}`);
    },
  });

  const errors = fieldErrors(save.error);
  const err = (k: string) => errors[k];

  if (isEdit && (existing.isPending || !loaded)) return existing.error ? <ErrorBox error={existing.error} /> : <Loading />;
  if (copyRef && !loaded) return source.error ? <ErrorBox error={source.error} /> : <Loading />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  const backTo = isEdit ? `/assets/${ref}` : copyRef ? `/assets/${copyRef}` : '/assets';

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        back={
          <Link to={backTo} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Zurück
          </Link>
        }
        title={isEdit ? `${form.asset_number} bearbeiten` : copyRef ? `Kopie von ${source.data?.asset_number ?? copyRef}` : 'Asset anlegen'}
      />

      <div className="space-y-5">
        {copyRef && (
          <Notice>
            <span className="flex items-start gap-2">
              <Copy className="mt-0.5 size-4 shrink-0" />
              <span>
                Alle Daten von <strong>{source.data?.asset_number}</strong> sind übernommen – noch ist nichts gespeichert. Leer gelassen sind, weil sie je Gerät
                eindeutig sind: Inventarnummer (wird automatisch vergeben), Seriennummer, Service Tag, Anlagennummer, Notizen, Mitarbeiter sowie Hostname, IP-/MAC-Adressen,
                IMEI, Rufnummer und Lizenzschlüssel. Fotos, Dokumente, Komponenten und Historie werden nicht kopiert.
              </span>
            </span>
          </Notice>
        )}
        <Card>
          <CardHeader title="Grunddaten" />
          <CardBody>
            <FormGrid>
              <Field label="Asset-Typ" required error={err('asset_type')}>
                {(p) => (
                  <Select
                    {...p}
                    value={form.asset_type}
                    onChange={(e) => {
                      const t = types.data?.find((x) => x.key === e.target.value);
                      setForm((f) => ({ ...f, asset_type: e.target.value, tracking_type: isEdit ? f.tracking_type : (t?.default_tracking_type ?? 'asset') }));
                    }}
                  >
                    <option value="">Bitte wählen …</option>
                    {types.data?.map((t) => (
                      <option key={t.id} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Bezeichnung" required error={err('name')} hint="z. B. „Notebook MK“ oder „Server Buchhaltung“">
                {(p) => <Input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
              </Field>
              <Field label="Hersteller" error={err('manufacturer')}>
                {(p) => <Input {...p} value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} />}
              </Field>
              <Field label="Modell" error={err('model')}>
                {(p) => <Input {...p} value={form.model} onChange={(e) => set('model', e.target.value)} />}
              </Field>
              <Field label="Seriennummer" error={err('serial_number')}>
                {(p) => <Input {...p} value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} />}
              </Field>
              <Field label="Service Tag" error={err('service_tag')}>
                {(p) => <Input {...p} value={form.service_tag} onChange={(e) => set('service_tag', e.target.value)} />}
              </Field>
              <Field label="Hersteller-Teilenummer" error={err('manufacturer_part_number')}>
                {(p) => <Input {...p} value={form.manufacturer_part_number} onChange={(e) => set('manufacturer_part_number', e.target.value)} />}
              </Field>
              <Field label="Art" hint="Komponenten (z. B. SSD, RAM) werden meist in ein Gerät eingebaut.">
                {(p) => (
                  <Select {...p} value={form.tracking_type} onChange={(e) => set('tracking_type', e.target.value as FormState['tracking_type'])}>
                    <option value="asset">Eigenständiges Gerät</option>
                    <option value="component">Komponente</option>
                  </Select>
                )}
              </Field>
              {!isEdit && (
                <Field label="Inventarnummer" error={err('asset_number')} hint="Leer lassen = automatisch vergeben. Nur für Altbestand mit vorhandener Nummer.">
                  {(p) => <Input {...p} value={form.asset_number} onChange={(e) => set('asset_number', e.target.value)} placeholder="automatisch" />}
                </Field>
              )}
              <Field label="Beschreibung" error={err('description')} className="sm:col-span-2">
                {(p) => <Textarea {...p} rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />}
              </Field>
              <div className="sm:col-span-2">
                <Checkbox
                  label="Kein Foto nötig"
                  checked={form.photo_not_required}
                  onChange={(e) => set('photo_not_required', e.target.checked)}
                />
                <p className="mt-0.5 pl-6 text-xs text-slate-500">Dann wird das Gerät in der Übersicht nicht als „Foto fehlt“ markiert.</p>
              </div>
            </FormGrid>
          </CardBody>
        </Card>

        {!isEdit && (
          <Card>
            <CardHeader title="Einsatz" description="Später über „Zuordnen“ und „Standort“ auf der Detailseite änderbar." />
            <CardBody>
              <FormGrid className="lg:grid-cols-4">
                <Field label="Status" error={err('status')}>
                  {(p) => (
                    <Select {...p} value={form.status} onChange={(e) => set('status', e.target.value as FormState['status'])}>
                      {(['in_stock', 'active', 'repair', 'retired', 'lost', 'unknown'] as const).map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Mitarbeiter" error={err('employee')}>
                  {(p) => <EmployeeSelect {...p} value={form.employee} onChange={(v) => setForm((f) => ({ ...f, employee: v, status: v && f.status === 'in_stock' ? 'active' : f.status }))} />}
                </Field>
                <Field label="Standort" error={err('location')}>
                  {(p) => <LocationSelect {...p} value={form.location} onChange={(v) => set('location', v)} />}
                </Field>
                <Field label="Gültig ab" error={err('effective_date')} hint="z. B. Übergabedatum">
                  {(p) => <DateInput {...p} max={todayIso()} value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} />}
                </Field>
              </FormGrid>
            </CardBody>
          </Card>
        )}

        {specDefs.length > 0 && (
          <Card>
            <CardHeader title="Technische Daten" description={type?.label} />
            <CardBody>
              <FormGrid className="lg:grid-cols-3">
                {specDefs.filter((d) => specVisible(d.key, specs)).map((d) => (
                  <SpecInput key={d.key} def={d} value={specs[d.key] ?? ''} onChange={(v) => setSpecs((s) => ({ ...s, [d.key]: v }))} error={err(`specs.${d.key}`)} />
                ))}
              </FormGrid>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader title="Kauf und Buchhaltung" />
          <CardBody>
            <FormGrid className="lg:grid-cols-3">
              <Field label="Kaufdatum" error={err('purchase_date')}>
                {(p) => <DateInput {...p} value={form.purchase_date} onChange={(e) => set('purchase_date', e.target.value)} />}
              </Field>
              <Field label="Inbetriebnahme" error={err('commissioning_date')}>
                {(p) => <DateInput {...p} value={form.commissioning_date} onChange={(e) => set('commissioning_date', e.target.value)} />}
              </Field>
              <Field label="Aktivierungsdatum (AHK)" error={err('capitalization_date')}>
                {(p) => <DateInput {...p} value={form.capitalization_date} onChange={(e) => set('capitalization_date', e.target.value)} />}
              </Field>
              <Field
                label="Kosten netto (€)"
                error={err('cost_net_cents')}
                hint={autoCost === 'net' ? `Automatisch: brutto ohne ${VAT_PERCENT} % USt.` : undefined}
              >
                {(p) => <Input {...p} inputMode="decimal" placeholder="0,00" value={form.cost_net} onChange={(e) => setCost('net', e.target.value)} />}
              </Field>
              <Field
                label="Kosten brutto (€)"
                error={err('cost_gross_cents')}
                hint={autoCost === 'gross' ? `Automatisch: netto + ${VAT_PERCENT} % USt.` : undefined}
              >
                {(p) => <Input {...p} inputMode="decimal" placeholder="0,00" value={form.cost_gross} onChange={(e) => setCost('gross', e.target.value)} />}
              </Field>
              <Field label="Garantie bis" error={err('warranty_until')}>
                {(p) => <DateInput {...p} value={form.warranty_until} onChange={(e) => set('warranty_until', e.target.value)} />}
              </Field>
              <Field label="Lieferant" error={err('supplier')}>
                {(p) => <Input {...p} value={form.supplier} onChange={(e) => set('supplier', e.target.value)} />}
              </Field>
              <Field label="Rechnungsnummer" error={err('invoice_number')}>
                {(p) => <Input {...p} value={form.invoice_number} onChange={(e) => set('invoice_number', e.target.value)} />}
              </Field>
              <Field label="Bestellnummer" error={err('order_number')}>
                {(p) => <Input {...p} value={form.order_number} onChange={(e) => set('order_number', e.target.value)} />}
              </Field>
              <Field
                label="Anlagennummer Buchhaltung"
                error={err('accounting_asset_number')}
                hint={
                  existing.data?.inherited_accounting && !form.accounting_asset_number
                    ? `Leer lassen: eingebaut, gehört über ${existing.data.inherited_accounting.via?.asset_number ?? "das Hauptgerät"} zur Anlagennummer ${existing.data.inherited_accounting.accounting_asset_number}.`
                    : undefined
                }
              >
                {(p) => <Input {...p} value={form.accounting_asset_number} onChange={(e) => set('accounting_asset_number', e.target.value)} />}
              </Field>
              <div className="flex flex-col justify-end pb-1">
                <Checkbox
                  label="Nicht im Inventarabgleich"
                  checked={form.exclude_from_reconciliation}
                  onChange={(e) => set('exclude_from_reconciliation', e.target.checked)}
                />
                <p className="mt-0.5 pl-6 text-xs text-slate-500">Bereits anderweitig abgerechnet – der Abgleich mit der Buchhaltung ignoriert das Gerät.</p>
              </div>
            </FormGrid>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Notizen" />
          <CardBody>
            <Textarea aria-label="Notizen" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </CardBody>
        </Card>

        <ErrorBox error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={() => navigate(backTo)}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!form.asset_type || !form.name.trim()}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </div>
    </form>
  );
}
